import { supabase } from '../lib/supabase';

/**
 * Current Agent Service - Provides dynamic access to the currently active agent's information
 * ✅ FIXED: Updated column names to match actual database schema
 */
class CurrentAgentService {
  constructor() {
    this.currentAgent = null;
    this.lastFetched = null;
    this.cacheExpiry = 15000; // 15 seconds for faster updates
    this.fallbackAgent = {
      full_name: 'Damian Sailors, CA LIC# 1N13041',
      phone: '(916) 930-6312',
      formatted_phone: '+19169306312',
      license_number: '1N13041'
    };
    
    // Set up real-time subscription for agent changes
    this.setupRealtimeSubscription();
    
    console.log('🚀 CurrentAgentService initialized with fallback:', this.fallbackAgent?.phone);
  }

  /**
   * ✅ FIXED: Get current agent with correct column names
   */
  async getCurrentAgent() {
    try {
      // Check cache first (15 second cache for faster updates)
      if (this.currentAgent && this.lastFetched && 
          (Date.now() - this.lastFetched) < this.cacheExpiry) {
        console.log('📦 Using cached agent:', this.currentAgent?.full_name, this.currentAgent?.phone);
        return this.currentAgent;
      }

      console.log('🔄 Fetching fresh agent data from database...');
      
      // ✅ FIXED: Use correct column names - is_currently_on_shift instead of is_on_shift
      // ✅ FIXED: Use status = 'on_shift' instead of is_active = true
      const { data, error } = await supabase
        ?.from('bail_agents')
        ?.select('*')
        ?.eq('is_currently_on_shift', true)
        ?.eq('status', 'on_shift')
        ?.single();
      
      if (!error && data) {
        this.currentAgent = {
          ...data,
          formatted_phone: this.formatPhoneForCall(data?.phone)
        };
        this.lastFetched = Date.now();
        console.log('✅ Fresh agent data loaded:', this.currentAgent?.full_name, this.currentAgent?.phone);
        return this.currentAgent;
      }

      console.warn('⚠️ No agent data from database, using cached or fallback');
      // Return cached data if available, otherwise fallback
      return this.currentAgent || this.fallbackAgent;
    } catch (error) {
      console.error('❌ Error fetching current agent:', error);
      return this.currentAgent || this.fallbackAgent;
    }
  }

  /**
   * ✅ ENHANCED: Get current agent's phone number in display format with debugging
   */
  async getCurrentAgentPhone() {
    try {
      const agent = await this.getCurrentAgent();
      const phone = agent?.phone || this.fallbackAgent?.phone;
      console.log('📞 getCurrentAgentPhone returning:', phone);
      return phone;
    } catch (error) {
      console.error('❌ Error getting agent phone:', error);
      return this.fallbackAgent?.phone;
    }
  }

  /**
   * ✅ ENHANCED: Get current agent's phone number in E.164 format for tel: links
   */
  async getCurrentAgentPhoneFormatted() {
    try {
      const agent = await this.getCurrentAgent();
      const formatted = agent?.formatted_phone || this.fallbackAgent?.formatted_phone;
      console.log('📱 getCurrentAgentPhoneFormatted returning:', formatted);
      return formatted;
    } catch (error) {
      console.error('❌ Error getting formatted phone:', error);
      return this.fallbackAgent?.formatted_phone;
    }
  }

  /**
   * ✅ ENHANCED: Get current agent's name with debugging
   */
  async getCurrentAgentName() {
    try {
      const agent = await this.getCurrentAgent();
      const name = agent?.full_name || this.fallbackAgent?.full_name;
      console.log('👤 getCurrentAgentName returning:', name);
      return name;
    } catch (error) {
      console.error('❌ Error getting agent name:', error);
      return this.fallbackAgent?.full_name;
    }
  }

  /**
   * ✅ ENHANCED: Get display text for phone with debugging
   */
  async getCurrentAgentPhoneText() {
    try {
      const agent = await this.getCurrentAgent();
      const name = agent?.full_name || 'Duty Agent';
      const phone = agent?.phone || this.fallbackAgent?.phone;
      const phoneText = `Call ${name} ${phone}`;
      console.log('📞📝 getCurrentAgentPhoneText returning:', phoneText);
      return phoneText;
    } catch (error) {
      console.error('❌ Error getting phone text:', error);
      return `Call ${this.fallbackAgent?.full_name} ${this.fallbackAgent?.phone}`;
    }
  }

  /**
   * 🔧 ENHANCED: Aggressive cache clearing for troubleshooting
   */
  clearCache() {
    console.log('🗑️ AGGRESSIVE CACHE CLEAR - Clearing all agent data...');
    this.currentAgent = null;
    this.lastFetched = null;
    
    // Clear any potential browser storage
    try {
      sessionStorage?.removeItem('currentAgent');
      localStorage?.removeItem('agentInitials');
      localStorage?.removeItem('currentAgentData');
    } catch (error) {
      console.log('Note: Could not clear browser storage:', error);
    }
    
    console.log('✅ All caches cleared');
  }

  /**
   * 🔧 FIXED: Force refresh with correct column names
   */
  async refresh() {
    console.log('🔄 FORCE REFRESH - Starting with database query...');
    this.clearCache(); // Use enhanced clearing
    
    try {
      // ✅ FIXED: Use correct column names
      const { data, error } = await supabase
        ?.from('bail_agents')
        ?.select('*')
        ?.eq('is_currently_on_shift', true)
        ?.eq('status', 'on_shift')
        ?.single();
      
      console.log('📊 Direct DB response:', { data, error });
      
      if (!error && data) {
        this.currentAgent = {
          ...data,
          formatted_phone: this.formatPhoneForCall(data?.phone)
        };
        this.lastFetched = Date.now();
        
        const initials = this.getInitialsFromName(this.currentAgent?.full_name);
        console.log('✅ FORCE REFRESH SUCCESS:', {
          name: this.currentAgent?.full_name,
          phone: this.currentAgent?.phone,
          initials: initials,
          timestamp: new Date()?.toISOString()
        });
        
        return this.currentAgent;
      } else {
        console.warn('⚠️ DB query returned no data or error:', { data, error });
        return this.fallbackAgent;
      }
    } catch (error) {
      console.error('❌ Force refresh failed:', error);
      return this.fallbackAgent;
    }
  }

  /**
   * 🔧 ENHANCED: Debug version of getCurrentAgentInitials
   */
  async getCurrentAgentInitials() {
    try {
      console.log('📝 Getting agent initials - starting fresh...');
      
      // Always get fresh data for initials
      const agent = await this.refresh();
      const name = agent?.full_name || this.fallbackAgent?.full_name;
      const initials = this.getInitialsFromName(name);
      
      console.log('📝 INITIALS DEBUG:', {
        agentName: name,
        calculatedInitials: initials,
        rawAgent: agent,
        timestamp: new Date()?.toISOString()
      });
      
      return initials;
    } catch (error) {
      console.error('❌ Error getting agent initials:', error);
      const fallbackInitials = this.getInitialsFromName(this.fallbackAgent?.full_name) || 'DS';
      console.log('🔄 Using fallback initials:', fallbackInitials);
      return fallbackInitials;
    }
  }

  /**
   * ✅ ENHANCED: Extract initials from full name with better fallback handling
   */
  getInitialsFromName(fullName) {
    if (!fullName) return 'DS'; // Default to Damian Sailors's initials
    
    const names = fullName?.trim()?.split(' ');
    if (names?.length >= 2) {
      // Get first letter of first name and first letter of last name
      return (names?.[0]?.[0] + names?.[names?.length - 1]?.[0])?.toUpperCase();
    } else if (names?.length === 1) {
      // If only one name, use first two letters
      return names?.[0]?.substring(0, 2)?.toUpperCase();
    }
    return 'DS'; // Fallback to current agent
  }

  /**
   * Format phone number for tel: links (E.164 format)
   */
  formatPhoneForCall(phone) {
    if (!phone) return this.fallbackAgent?.formatted_phone;
    // Remove all non-digits and format as E.164
    const digits = phone?.replace(/[^\d]/g, '');
    if (digits?.length === 10) {
      return `+1${digits}`;
    }
    if (digits?.length === 11 && digits?.startsWith('1')) {
      return `+${digits}`;
    }
    return this.fallbackAgent?.formatted_phone;
  }

  /**
   * Set up real-time subscription for agent changes
   */
  setupRealtimeSubscription() {
    try {
      supabase
        ?.channel('current_agent_service')
        ?.on('postgres_changes', 
          { 
            event: '*',
            schema: 'public', 
            table: 'bail_agents'
          }, 
          (payload) => {
            console.log('Agent data changed, clearing cache:', payload);
            // Clear cache when any agent data changes
            this.currentAgent = null;
            this.lastFetched = null;
            
            // Dispatch custom event for components to refresh
            if (typeof window !== 'undefined') {
              window?.dispatchEvent(new CustomEvent('currentAgentChanged', { 
                detail: { 
                  timestamp: Date.now(),
                  type: payload?.eventType 
                }
              }));
            }
          }
        )
        ?.subscribe();
    } catch (error) {
      console.warn('Failed to setup real-time subscription:', error);
    }
  }
}

// Export singleton instance
export const currentAgentService = new CurrentAgentService();
export default currentAgentService;